import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { FaStickyNote } from 'react-icons/fa';
import {
  RESERVATION_STATUS_LABELS,
  setReservationServiceStatus,
  updateReservation,
} from '../../../../services/reservationsApi';
import { createServiceReview } from '../../../../services/reviewsApi';
import TableColumnMenu from '../../../../components/TableColumnMenu';
import useTableColumns from '../../../../hooks/useTableColumns';

const formatDate = value => {
  if (!value) return 'Sin definir';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin definir';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = value => {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin registro';
  return date.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sanitizePhoneNumber = value => {
  if (!value) return '';
  return String(value).replace(/[^\d+]/g, '');
};

const buildTelHref = value => {
  const digits = sanitizePhoneNumber(value);
  return digits ? `tel:${digits}` : '';
};

const buildWhatsAppHref = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');
  return digits ? `https://wa.me/${digits}` : '';
};

const normalizeWebsiteHref = value => {
  if (!value) return '';
  try {
    const url = value.startsWith('http') ? new URL(value) : new URL(`https://${value}`);
    return url.href;
  } catch {
    if (!value.startsWith('http')) {
      return `https://${value}`;
    }
    return value;
  }
};

const DEFAULT_CURRENCY = 'MXN';
const IN_PROGRESS_STATUSES = [
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
  'intransit',
  'in_transit',
  'ongoing',
  'running',
  'live',
  'en_ruta',
];
const FINALIZED_STATUSES = [
  'finalized',
  'finalizada',
  'finalizado',
  'completed',
  'complete',
  'finished',
  'confirmed',
];
const REJECTED_STATUSES = ['rejected', 'declined'];
const CANCELLED_STATUSES = ['cancelled', 'canceled'];
const SERVICE_STATUS_LABELS = {
  unknown: 'Confirma tu servicio',
  completed: 'Servicio confirmado',
  not_completed: 'Servicio no realizado',
};
const REVIEW_FIELDS = [
  { key: 'overall', label: 'Experiencia general' },
  { key: 'quality', label: 'Calidad' },
  { key: 'price', label: 'Precio' },
  { key: 'punctuality', label: 'Puntualidad' },
];
const RATING_SCALE = [1, 2, 3, 4, 5];
const REVIEW_INITIAL_STATE = {
  overall: 0,
  quality: 0,
  price: 0,
  punctuality: 0,
  comment: '',
};

const ReviewSummary = ({ review }) => {
  if (!review) return null;
  return (
    <div className="review-summary">
      <div className="review-summary__grid">
        {REVIEW_FIELDS.map(field => (
          <div key={field.key} className="review-summary__item">
            <span>{field.label}</span>
            <strong>{review[field.key] ?? '—'}</strong>
          </div>
        ))}
      </div>
      {review.comment ? (
        <p className="review-summary__comment">“{review.comment}”</p>
      ) : null}
    </div>
  );
};

const normalizeStatus = status => (status || '').toString().trim().toLowerCase();

const parseCurrencyValue = value => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const digits = value.replace(/[\s,]/g, '').replace(/[^\d.-]/g, '');
    if (!digits) return null;
    const numeric = Number(digits);
    return Number.isNaN(numeric) ? null : numeric;
  }
  return null;
};

const formatCurrencyValue = (amount, currency = DEFAULT_CURRENCY) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return '';
  }
  const normalizedCurrency = typeof currency === 'string' && currency.trim().length === 3
    ? currency.trim().toUpperCase()
    : DEFAULT_CURRENCY;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }
};

const resolveCostLabel = reservation => {
  if (!reservation) return 'Pendiente';
  const numericCandidates = [
    reservation.quoteAmount,
    reservation.totalCost,
    reservation.budgetAmount,
  ];
  for (const candidate of numericCandidates) {
    const numeric = parseCurrencyValue(candidate);
    if (numeric !== null) {
      return formatCurrencyValue(
        numeric,
        reservation.quoteCurrency || reservation.currency || reservation.budgetCurrency || DEFAULT_CURRENCY
      );
    }
  }

  if (reservation.budgetLabel) {
    return reservation.budgetLabel;
  }
  if (reservation.budget) {
    return reservation.budget;
  }
  return 'Pendiente';
};

const buildRouteSummary = reservation => {
  const origin = reservation.pickupLocation || 'Origen por definir';
  const destination = reservation.dropoffLocation || 'Destino por definir';
  if (!reservation.pickupLocation && !reservation.dropoffLocation) {
    return reservation.publication?.title || reservation.unit?.model || 'Ruta por definir';
  }
  return `${origin} → ${destination}`;
};

const getProviderInitials = value => {
  if (!value) return 'PR';
  const tokens = value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(token => token[0]?.toUpperCase() || '');
  return tokens.join('') || 'PR';
};

const resolveStatusTone = status => {
  const normalized = normalizeStatus(status);
  if (normalized === 'pending') return 'warning';
  if (normalized === 'accepted') return 'success';
  if (IN_PROGRESS_STATUSES.includes(normalized)) return 'info';
  if (REJECTED_STATUSES.includes(normalized) || CANCELLED_STATUSES.includes(normalized)) {
    return 'muted';
  }
  if (FINALIZED_STATUSES.includes(normalized)) {
    return 'neutral';
  }
  return 'muted';
};

const getCostNumericValue = reservation => {
  if (!reservation) return null;
  const numericCandidates = [reservation.quoteAmount, reservation.totalCost, reservation.budgetAmount];
  for (const candidate of numericCandidates) {
    const parsed = parseCurrencyValue(candidate);
    if (parsed !== null) {
      return parsed;
    }
  }
  const fallback = parseCurrencyValue(reservation.budget);
  return fallback !== null ? fallback : null;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const NON_SORTABLE_COLUMNS = new Set(['actions']);
const CUSTOMER_RESERVATION_COLUMN_DEFAULTS = [
  { key: 'journey', label: 'Reserva / Ruta', width: 320, minWidth: 240 },
  { key: 'provider', label: 'Proveedor', width: 240, minWidth: 200 },
  { key: 'dates', label: 'Fechas', width: 220, minWidth: 200 },
  { key: 'status', label: 'Estado', width: 200, minWidth: 180 },
  { key: 'actions', label: 'Accion', width: 180, minWidth: 150 },
];

const NOOP = () => {};

export default function CustomerReservationList({
  items = [],
  loading = false,
  emptyMessage = 'Aun no registras reservas.',
  initialReservationId = '',
  onReservationFocus = NOOP,
  onReservationUpdate = NOOP,
  footerTitle = '',
}) {
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({ key: 'journey', direction: 'asc' });
  const [activeColumnMenu, setActiveColumnMenu] = useState('');
  const [serviceStatusSubmitting, setServiceStatusSubmitting] = useState(false);
  const [serviceStatusError, setServiceStatusError] = useState('');
  const [reviewForm, setReviewForm] = useState(REVIEW_INITIAL_STATE);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const columnHeaderRefs = useRef({});
  const columnResizeStateRef = useRef({
    columnKey: '',
    startX: 0,
    startWidth: 0,
  });

  const {
    columns: customerReservationColumns,
    visibleColumns: visibleCustomerReservationColumns,
    moveColumn: moveCustomerReservationColumn,
    toggleColumn: toggleCustomerReservationColumn,
    setColumnWidth: setCustomerReservationColumnWidth,
    resetColumns: resetCustomerReservationColumns,
  } = useTableColumns('reservations.customer.v3', CUSTOMER_RESERVATION_COLUMN_DEFAULTS);

  useEffect(() => {
    if (!initialReservationId) {
      return;
    }
    const exists = items.some(item => item.id === initialReservationId);
    if (!exists) {
      return;
    }
    setSelectedReservationId(initialReservationId);
    onReservationFocus(initialReservationId);
  }, [initialReservationId, items, onReservationFocus]);

  const selectedReservation = useMemo(
    () => items.find(item => item.id === selectedReservationId) || null,
    [items, selectedReservationId]
  );

  useEffect(() => {
    setServiceStatusError('');
    setReviewError('');
    setServiceStatusSubmitting(false);
    setReviewSubmitting(false);
    if (!selectedReservation || selectedReservation.clientReview) {
      setReviewForm(REVIEW_INITIAL_STATE);
      return;
    }
    setReviewForm(REVIEW_INITIAL_STATE);
  }, [selectedReservation]);

  const serviceStatusKey = selectedReservation
    ? (selectedReservation.serviceStatus || 'unknown').toLowerCase()
    : 'unknown';
  const serviceStatusLabel =
    SERVICE_STATUS_LABELS[serviceStatusKey] || SERVICE_STATUS_LABELS.unknown;
  const serviceConfirmedAtLabel = selectedReservation?.serviceStatusConfirmedAt
    ? formatDateTime(selectedReservation.serviceStatusConfirmedAt)
    : '';
  const clientReviewExists = Boolean(selectedReservation?.clientReview);
  const providerContactItems = useMemo(() => {
    if (!selectedReservation) {
      return [];
    }
    const company = selectedReservation.company || {};
    const contactName = company.contactName || '';
    const phoneNumber = company.contactPhone || company.phoneNumber || '';
    const email = company.contactEmail || '';
    const whatsapp = company.whatsapp || '';
    const website = company.websiteUrl || '';

    const items = [];
    if (contactName) {
      items.push({
        key: 'contactName',
        label: 'Persona de contacto',
        value: contactName,
      });
    }
    if (phoneNumber) {
      items.push({
        key: 'phone',
        label: 'Telefono',
        value: phoneNumber,
        href: buildTelHref(phoneNumber),
      });
    }
    if (email) {
      items.push({
        key: 'email',
        label: 'Correo',
        value: email,
        href: `mailto:${email}`,
      });
    }
    if (whatsapp) {
      items.push({
        key: 'whatsapp',
        label: 'WhatsApp',
        value: whatsapp,
        href: buildWhatsAppHref(whatsapp),
        target: '_blank',
      });
    }
    if (website) {
      items.push({
        key: 'website',
        label: 'Sitio web',
        value: website,
        href: normalizeWebsiteHref(website),
        target: '_blank',
      });
    }
    return items;
  }, [selectedReservation]);

  const toggleColumnMenu = useCallback(columnKey => {
    setActiveColumnMenu(prev => (prev === columnKey ? '' : columnKey));
  }, []);

  const handleColumnContextMenu = useCallback(
    (event, columnKey) => {
      event.preventDefault();
      event.stopPropagation();
      toggleColumnMenu(columnKey);
    },
    [toggleColumnMenu]
  );

  const handleColumnSort = useCallback(columnKey => {
    if (NON_SORTABLE_COLUMNS.has(columnKey)) {
      return;
    }
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, []);

  const handleColumnHeaderKeyDown = useCallback(
    (event, columnKey) => {
      if (NON_SORTABLE_COLUMNS.has(columnKey)) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleColumnSort(columnKey);
        return;
      }
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault();
        toggleColumnMenu(columnKey);
      }
    },
    [handleColumnSort, toggleColumnMenu]
  );

  const beginColumnResize = useCallback((clientX, column) => {
    if (typeof clientX !== 'number') return;
    columnResizeStateRef.current = {
      columnKey: column.key,
      startX: clientX,
      startWidth: column.width || column.minWidth || 180,
    };
    if (typeof document !== 'undefined') {
      document.body.classList.add('is-resizing-column');
    }
  }, []);

  const handleColumnResizeMouseDown = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.clientX, column);
    },
    [beginColumnResize]
  );

  const handleColumnResizeTouchStart = useCallback(
    (event, column) => {
      if (!event.touches || event.touches.length !== 1) return;
      event.preventDefault();
      event.stopPropagation();
      beginColumnResize(event.touches[0].clientX, column);
    },
    [beginColumnResize]
  );

  useEffect(() => {
    const stopResizing = () => {
      if (!columnResizeStateRef.current.columnKey) return;
      columnResizeStateRef.current = {
        columnKey: '',
        startX: 0,
        startWidth: 0,
      };
      if (typeof document !== 'undefined') {
        document.body.classList.remove('is-resizing-column');
      }
    };

    const handlePointerMove = event => {
      const { columnKey, startX, startWidth } = columnResizeStateRef.current;
      if (!columnKey) return;
      const clientX =
        'touches' in event ? event.touches?.[0]?.clientX : event.clientX;
      if (typeof clientX !== 'number') return;
      event.preventDefault();
      const delta = clientX - startX;
      setCustomerReservationColumnWidth(columnKey, startWidth + delta);
    };

    const handlePointerUp = event => {
      if (!columnResizeStateRef.current.columnKey) return;
      if (event) {
        event.preventDefault();
      }
      stopResizing();
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    window.addEventListener('blur', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
      window.removeEventListener('blur', stopResizing);
      stopResizing();
    };
  }, [setCustomerReservationColumnWidth]);

  const handlePageSizeChange = useCallback(event => {
    setPageSize(Number(event.target.value));
  }, []);

  const getSortValue = useCallback(
    item => {
      if (!item) return '';
      switch (sortConfig.key) {
        case 'journey': {
          const label = [
            item.id,
            item.pickupLocation,
            item.dropoffLocation,
            item.publication?.title,
            item.unit?.model,
            item.unit?.tractorType,
          ]
            .filter(Boolean)
            .join(' ');
          return label.toString().toLowerCase();
        }
        case 'dates': {
          const timestamp = item.requestedFrom ? new Date(item.requestedFrom).getTime() : 0;
          return Number.isNaN(timestamp) ? 0 : timestamp;
        }
        case 'provider': {
          const providerName =
            item.company?.name || item.publication?.companyName || 'Proveedor sin nombre';
          return providerName.toString().toLowerCase();
        }
        case 'cost': {
          const numeric = getCostNumericValue(item);
          return numeric === null ? 0 : numeric;
        }
        case 'status': {
          const label = RESERVATION_STATUS_LABELS[item.status] || item.status || '';
          return label.toString().toLowerCase();
        }
        case 'actions':
          return 0;
        default:
          return '';
      }
    },
    [sortConfig.key]
  );

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const next = [...items];
    if (!sortConfig?.key) return next;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        if (valueA === valueB) return 0;
        return valueA > valueB ? direction : -direction;
      }
      const stringA = valueA?.toString().toLowerCase() || '';
      const stringB = valueB?.toString().toLowerCase() || '';
      const comparison = stringA.localeCompare(stringB, 'es', { sensitivity: 'base' });
      if (comparison === 0) return 0;
      return comparison > 0 ? direction : -direction;
    });
    return next;
  }, [getSortValue, items, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [pageSize, sortConfig]);

  const paginatedItems = useMemo(() => {
    if (loading) return [];
    const start = page * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [loading, page, pageSize, sortedItems]);

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index),
    [totalPages]
  );

  const showEmptyState = !loading && items.length === 0;

  const renderPlaceholderRow = useCallback(
    key => (
      <tr className="table-placeholder-empty" key={`placeholder-${key}`}>
        <td colSpan={Math.max(1, visibleCustomerReservationColumns.length)} aria-hidden="true">
          &nbsp;
        </td>
      </tr>
    ),
    [visibleCustomerReservationColumns.length]
  );

  const handleServiceStatusResponse = useCallback(
    async completed => {
      if (!selectedReservation) return;
      setServiceStatusError('');
      setServiceStatusSubmitting(true);
      try {
        const updated = await setReservationServiceStatus(selectedReservation.id, completed);
        if (typeof onReservationUpdate === 'function') {
          onReservationUpdate(updated);
        }
      } catch (error) {
        setServiceStatusError(
          error?.message || 'No pudimos guardar tu respuesta. Intenta nuevamente.'
        );
      } finally {
        setServiceStatusSubmitting(false);
      }
    },
    [onReservationUpdate, selectedReservation]
  );

  const handleReviewRatingChange = useCallback((field, value) => {
    setReviewForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleReviewCommentChange = useCallback(event => {
    const { value } = event.target;
    setReviewForm(prev => ({
      ...prev,
      comment: value,
    }));
  }, []);

  const handleClientReviewSubmit = useCallback(
    async event => {
      event.preventDefault();
      if (!selectedReservation) return;
      setReviewError('');
      const missingField = REVIEW_FIELDS.find(field => (reviewForm[field.key] || 0) < 1);
      if (missingField) {
        setReviewError('Selecciona una calificación en cada criterio.');
        return;
      }
      setReviewSubmitting(true);
      try {
        const targetCompanyId = selectedReservation.companyId || selectedReservation.company?.id || '';
        if (!targetCompanyId) {
          throw new Error('No pudimos identificar al proveedor a calificar.');
        }
        const review = await createServiceReview({
          reservationId: selectedReservation.id,
          direction: 'client_to_provider',
          targetCompanyId,
          overall: reviewForm.overall,
          quality: reviewForm.quality,
          price: reviewForm.price,
          punctuality: reviewForm.punctuality,
          comment: reviewForm.comment,
        });
        const updated = await updateReservation(selectedReservation.id, {
          clientReview: review.id,
        });
        if (typeof onReservationUpdate === 'function') {
          onReservationUpdate(updated);
        }
        setReviewForm(REVIEW_INITIAL_STATE);
      } catch (error) {
        setReviewError(error?.message || 'No pudimos registrar tu reseña. Intenta nuevamente.');
      } finally {
        setReviewSubmitting(false);
      }
    },
    [onReservationUpdate, reviewForm, selectedReservation]
  );

  const handleCloseModal = () => {
    setSelectedReservationId(null);
  };

  return (
    <>
      <div className="table-scroll-hint" role="note">
        <span aria-hidden="true" className="table-scroll-hint__icon" />
        <span className="table-scroll-hint__text">
          Desliza horizontalmente, haz clic en un encabezado para ordenar (A-Z/Z-A), clic derecho para
          configurar columnas o arrastra el borde para ajustar el ancho.
        </span>
      </div>

      <div className="reservations-table-wrapper client-reservations-table-wrapper">
        <table className="reservations-table client-reservations-table">
          <thead>
            <tr>
              {visibleCustomerReservationColumns.map(column => {
                const isSorted = sortConfig.key === column.key;
                const isSortable = !NON_SORTABLE_COLUMNS.has(column.key);
                const ariaSort =
                  isSortable && isSorted
                    ? sortConfig.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none';
                return (
                  <th
                    key={column.key}
                    ref={element => {
                      if (element) {
                        columnHeaderRefs.current[column.key] = element;
                      }
                    }}
                    style={{ minWidth: `${column.width}px`, width: `${column.width}px` }}
                    className={`table-column-header client-table-header client-table-header--${column.key}`}
                    onClick={isSortable ? () => handleColumnSort(column.key) : undefined}
                    onContextMenu={event => handleColumnContextMenu(event, column.key)}
                    onKeyDown={event => handleColumnHeaderKeyDown(event, column.key)}
                    tabIndex={0}
                    aria-sort={ariaSort}
                    title={
                      isSortable
                        ? 'Haz clic para ordenar (A-Z/Z-A). Clic derecho abre la configuracion de columnas.'
                        : 'Clic derecho abre la configuracion de columnas.'
                    }
                  >
                    <span className="table-column-header__label">
                      {column.label}
                      {isSortable ? (
                        <span
                          className={`table-column-header__sort${
                            isSorted ? ` table-column-header__sort--${sortConfig.direction}` : ''
                          }`}
                          aria-hidden="true"
                        >
                          {isSorted ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      ) : null}
                      {isSortable && isSorted ? (
                        <span className="visually-hidden">
                          {`Ordenado ${
                            sortConfig.direction === 'asc' ? 'ascendente' : 'descendente'
                          }`}
                        </span>
                      ) : null}
                    </span>
                    <span
                      role="presentation"
                      className="table-column-header__resizer"
                      onMouseDown={event => handleColumnResizeMouseDown(event, column)}
                      onTouchStart={event => handleColumnResizeTouchStart(event, column)}
                      aria-hidden="true"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, index) =>
                renderPlaceholderRow(`loading-${index}`)
              )
            ) : (
              <>
                {paginatedItems.map(item => {
                  const statusLabel = RESERVATION_STATUS_LABELS[item.status] || item.status;
                  const providerName =
                    item.company?.name || item.publication?.companyName || 'Proveedor sin nombre';
                  const providerLogo = item.company?.logoUrl || '';
                  const providerInitials = getProviderInitials(providerName);
                  const routeSummary = buildRouteSummary(item);
                  const costLabel = resolveCostLabel(item);
                  const statusTone = resolveStatusTone(item.status);

                  const columnContent = {
                    journey: (
                      <div className="client-journey-cell">
                        <span className="client-journey-cell__id">#{item.id}</span>
                        <p className="client-journey-cell__route">{routeSummary}</p>
                        <span className="client-journey-cell__meta">
                          {item.publication?.title ||
                            item.unit?.model ||
                            item.unit?.tractorType ||
                            'Viaje por definir'}
                        </span>
                      </div>
                    ),
                    provider: (
                      <div className="client-provider-cell">
                        <div className="client-provider-avatar" aria-hidden="true">
                          {providerLogo ? (
                            <img src={providerLogo} alt="" />
                          ) : (
                            <span>{providerInitials}</span>
                          )}
                        </div>
                        <div className="client-provider-cell__details">
                          <strong>{providerName}</strong>
                          <span>{item.company?.category || 'Proveedor Flotte'}</span>
                        </div>
                      </div>
                    ),
                    dates: (
                      <div className="client-dates-cell">
                        <strong>
                          {formatDate(item.requestedFrom)}
                          {item.requestedTo ? ` → ${formatDate(item.requestedTo)}` : ''}
                        </strong>
                        <span>Solicitada {formatDateTime(item.created)}</span>
                      </div>
                    ),
                    cost: (
                      <div className="client-cost-cell">
                        <strong className="client-cost-value">{costLabel}</strong>
                        <span className="client-cost-meta">Cotizacion estimada</span>
                      </div>
                    ),
                    status: (
                      <div className="client-status-cell">
                        <span className={`client-status-pill client-status-pill--${statusTone}`}>
                          {statusLabel}
                        </span>
                        <time dateTime={item.updated}>Actualizada {formatDateTime(item.updated)}</time>
                      </div>
                    ),
                    actions: (
                      <div className="client-actions-cell">
                        <button
                          type="button"
                          className="client-row-action-btn"
                          onClick={event => {
                            event.stopPropagation();
                            setSelectedReservationId(item.id);
                          }}
                        >
                          Ver detalles
                        </button>
                      </div>
                    ),
                  };

                  return (
                    <tr
                      key={item.id}
                      className="reservations-row client-reservations-row"
                      onClick={() => setSelectedReservationId(item.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedReservationId(item.id);
                        }
                      }}
                      data-clickable="true"
                    >
                      {visibleCustomerReservationColumns.map(column => (
                        <td
                          key={`${item.id}-${column.key}`}
                          className={`client-reservations-cell client-reservations-cell--${column.key}`}
                          style={{
                            minWidth: `${column.width}px`,
                            width: `${column.width}px`,
                          }}
                        >
                          {columnContent[column.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {showEmptyState ? (
                  <tr className="reservations-table-empty">
                    <td colSpan={Math.max(1, visibleCustomerReservationColumns.length)}>
                      <FaStickyNote aria-hidden="true" />
                      <p>{emptyMessage}</p>
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="reservations-table-footer client-reservations-table-footer">
        {footerTitle ? (
          <h2 className="reservations-toolbar__title reservations-table-footer__title">{footerTitle}</h2>
        ) : null}
        <div className="table-pagination">
          <div className="table-pagination__page-size">
            <label>
              Mostrar
              <select value={pageSize} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              reservas
            </label>
          </div>
          <div className="table-pagination__controls" role="navigation" aria-label="Paginacion de reservas">
            <button
              type="button"
              onClick={() => setPage(prev => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              Anterior
            </button>
            <div className="table-pagination__pages">
              {pageNumbers.map(number => (
                <button
                  type="button"
                  key={`customer-reservation-page-${number}`}
                  className={number === page ? 'active' : ''}
                  aria-current={number === page ? 'page' : undefined}
                  onClick={() => setPage(number)}
                >
                  {number + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
              disabled={page >= totalPages - 1}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
      <TableColumnMenu
        anchorElement={columnHeaderRefs.current[activeColumnMenu] || null}
        activeColumnKey={activeColumnMenu}
        columns={customerReservationColumns}
        onClose={() => setActiveColumnMenu('')}
        onMove={moveCustomerReservationColumn}
        onToggle={toggleCustomerReservationColumn}
        onWidthChange={setCustomerReservationColumnWidth}
        onReset={resetCustomerReservationColumns}
      />

      {selectedReservation ? (
        <div className="reservations-modal" role="dialog" aria-modal="true">
          <div className="reservations-modal__backdrop" onClick={handleCloseModal} />
          <div className="reservations-modal__dialog">
            <header className="reservations-modal__header">
              <div>
                <h3>{selectedReservation.publication?.title || 'Publicacion sin titulo'}</h3>
                <p>
                  {selectedReservation.unit?.model ||
                    selectedReservation.unit?.tractorType ||
                    selectedReservation.unit?.economicNumber ||
                    'Unidad por asignar'}
                </p>
              </div>
              <button
                type="button"
                className="reservations-modal__close"
                onClick={handleCloseModal}
                aria-label="Cerrar detalles"
              >
                ×
              </button>
            </header>

            <div className="reservations-modal__content">
              <section className="reservations-modal__grid">
                <div>
                  <h4>Fechas</h4>
                  <p>
                    {formatDate(selectedReservation.requestedFrom)}
                    {selectedReservation.requestedTo
                      ? ` → ${formatDate(selectedReservation.requestedTo)}`
                      : ''}
                  </p>
                  <p className="reservations-modal__meta">
                    Registrada: {formatDateTime(selectedReservation.created)}
                  </p>
                </div>
                <div>
                  <h4>Trayecto</h4>
                  <p>
                    <strong>Origen:</strong> {selectedReservation.pickupLocation || 'Sin definir'}
                  </p>
                  <p>
                    <strong>Destino:</strong> {selectedReservation.dropoffLocation || 'Sin definir'}
                  </p>
                </div>
                <div>
                  <h4>Detalle de carga</h4>
                  <p>{selectedReservation.cargoDescription || 'Sin descripcion'}</p>
                  {selectedReservation.specialRequirements ? (
                    <p>
                      <strong>Requisitos:</strong> {selectedReservation.specialRequirements}
                    </p>
                  ) : null}
                </div>
                <div>
                  <h4>Proveedor</h4>
                  <p>{selectedReservation.company?.name || 'Proveedor sin nombre'}</p>
                  {selectedReservation.company?.slug ? (
                    <a
                      className="reservations-modal__link"
                      href={`/empresa/${selectedReservation.company.slug}`}
                    >
                      Ver perfil de empresa
                    </a>
                  ) : null}
                </div>
              </section>

              <section className="reservations-modal__contact">
                <h4>Contacto del proveedor</h4>
                {selectedReservation.status === 'accepted' ? (
                  providerContactItems.length ? (
                    <ul className="reservations-contact-list">
                      {providerContactItems.map(item => (
                        <li key={item.key}>
                          <span>{item.label}</span>
                          {item.href ? (
                            <a
                              className="reservations-modal__contact-link"
                              href={item.href}
                              target={item.target || '_self'}
                              rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                            >
                              {item.value}
                            </a>
                          ) : (
                            <strong>{item.value}</strong>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="reservations-contact-placeholder">
                      El proveedor aun no ha compartido datos de contacto.
                    </p>
                  )
                ) : (
                  <p className="reservations-contact-placeholder">
                    Veras los datos de contacto cuando la reserva sea aceptada.
                  </p>
                )}
              </section>

              <section className="reservations-modal__notes">
                <h4>Notas del proveedor</h4>
                <p>{selectedReservation.decisionNotes || 'El proveedor no ha dejado notas.'}</p>
              </section>

              <section className="reservation-ticket__service">
                <div className="reservation-ticket__service-header">
                  <h4>Seguimiento de servicio</h4>
                  <span className={`service-status-chip service-status-chip--${serviceStatusKey}`}>
                    {serviceStatusLabel}
                  </span>
                </div>
                {selectedReservation.status !== 'confirmed' && serviceStatusKey === 'unknown' ? (
                  <p className="reservation-ticket__service-text">
                    El proveedor aún no marca el servicio como completado. Te avisaremos cuando esté
                    listo para evaluación.
                  </p>
                ) : null}
                {selectedReservation.status === 'confirmed' && serviceStatusKey === 'unknown' ? (
                  <div className="review-card">
                    <h5>¿Se completó el servicio?</h5>
                    <p className="review-card__description">
                      Notifica al proveedor para habilitar la evaluación de ambos.
                    </p>
                    {serviceStatusError ? (
                      <p className="review-card__error">{serviceStatusError}</p>
                    ) : null}
                    <div className="service-confirmation-actions">
                      <button
                        type="button"
                        onClick={() => handleServiceStatusResponse(true)}
                        disabled={serviceStatusSubmitting}
                      >
                        {serviceStatusSubmitting ? 'Enviando...' : 'Sí, se completó'}
                      </button>
                      <button
                        type="button"
                        className="service-confirmation-actions__decline"
                        onClick={() => handleServiceStatusResponse(false)}
                        disabled={serviceStatusSubmitting}
                      >
                        Reportar problema
                      </button>
                    </div>
                  </div>
                ) : null}
                {serviceStatusKey === 'not_completed' ? (
                  <p className="reservation-ticket__service-text reservation-ticket__service-text--warning">
                    Reportaste que el servicio no se completó. Nuestro equipo dará seguimiento contigo.
                  </p>
                ) : null}
                {serviceConfirmedAtLabel ? (
                  <p className="reservation-ticket__service-meta">
                    Registrado: {serviceConfirmedAtLabel}
                  </p>
                ) : null}
                {serviceStatusKey === 'completed' ? (
                  clientReviewExists ? (
                    <div className="review-card review-card--summary">
                      <h5>Tu evaluación</h5>
                      <ReviewSummary review={selectedReservation.clientReview} />
                    </div>
                  ) : (
                    <form className="review-card" onSubmit={handleClientReviewSubmit}>
                      <h5>Califica al proveedor</h5>
                      <p className="review-card__description">
                        Tu opinión ayuda a otros clientes a contratar con confianza.
                      </p>
                      <div className="review-card__grid">
                        {REVIEW_FIELDS.map(field => (
                          <div key={field.key} className="review-rating-field">
                            <span>{field.label}</span>
                            <div className="review-rating-scale">
                              {RATING_SCALE.map(score => (
                                <button
                                  type="button"
                                  key={`${field.key}-${score}`}
                                  className={`review-rating-btn${
                                    reviewForm[field.key] === score ? ' is-active' : ''
                                  }`}
                                  aria-pressed={reviewForm[field.key] === score}
                                  onClick={() => handleReviewRatingChange(field.key, score)}
                                >
                                  {score}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <label className="review-card__comment">
                        Comentario (opcional)
                        <textarea
                          rows={3}
                          value={reviewForm.comment}
                          onChange={handleReviewCommentChange}
                          placeholder="¿Qué destacarías del servicio?"
                        />
                      </label>
                      {reviewError ? <p className="review-card__error">{reviewError}</p> : null}
                      <div className="review-card__actions">
                        <button type="submit" disabled={reviewSubmitting}>
                          {reviewSubmitting ? 'Enviando reseña...' : 'Enviar reseña'}
                        </button>
                      </div>
                    </form>
                  )
                ) : null}
              </section>
            </div>

            <footer className="reservations-modal__footer">
              <div className="reservations-modal__status">
                <span className={`status-chip status-${selectedReservation.status}`}>
                  {RESERVATION_STATUS_LABELS[selectedReservation.status] ||
                    selectedReservation.status}
                </span>
                <time>
                  Actualizada: {formatDateTime(selectedReservation.updated)}
                </time>
              </div>
              {selectedReservation.status !== 'pending' && selectedReservation.decisionAt ? (
                <p className="reservations-modal__resolved">
                  Resolucion: {formatDateTime(selectedReservation.decisionAt)}
                </p>
              ) : null}
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
